import React, { useState, useRef, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { DropdownMenuItem } from '../ui/dropdown-menu';
import Pagination from '../common/Pagination';
import { Download, Upload, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle, RefreshCw, Pencil, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useMMDLocations } from '../../hooks/useMMDLocations';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const INLINE_PREVIEW_CHARS = 140;
const GRADE_OPTIONS = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

// Generate "BulkAnswer_<Grade>_<Function>_YYYY-MM-DD_HH-mm" stamp in IST (UTC+5:30).
// We compute IST without depending on the browser's locale by adding 5h30m to UTC.
const buildIstFilename = (grade, functionName, questionType) => {
  const safe = (s) => (s ? String(s).replace(/[^A-Za-z0-9]+/g, '') : 'All');
  const now = new Date();
  const istMs = now.getTime() + 5.5 * 60 * 60 * 1000;
  const ist = new Date(istMs);
  const yyyy = ist.getUTCFullYear();
  const mm = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(ist.getUTCDate()).padStart(2, '0');
  const HH = String(ist.getUTCHours()).padStart(2, '0');
  const MM = String(ist.getUTCMinutes()).padStart(2, '0');
  const stamp = `${yyyy}-${mm}-${dd}_${HH}-${MM}`;
  const qt = questionType === 'written' ? 'Written' : 'Oral';
  return `BulkAnswer_${qt}_${safe(grade)}_${safe(functionName)}_${stamp}.csv`;
};

/**
 * Bulk Answering control set for super-admins.
 * Renders two buttons (Template + Upload) and self-manages both dialogs/modal.
 * questionType: 'oral' | 'written'
 * compact: when true, render the two triggers as DropdownMenuItem rows so
 *   they fit inside a parent "More actions" menu (used by Written Mgmt to
 *   keep the header to a single row). Default false preserves the original
 *   side-by-side buttons used everywhere else.
 */
const BulkAnswerControls = ({ questionType = 'oral', onCommitted, compact = false }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Template dialog state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tplGrade, setTplGrade] = useState('all');
  const [tplFunctionId, setTplFunctionId] = useState('all');
  const [tplMmdLocation, setTplMmdLocation] = useState('all');
  const [tplLimit, setTplLimit] = useState(''); // empty string = All
  const [tplUnansweredOnly, setTplUnansweredOnly] = useState(true);

  // MMD Location list — only relevant for oral (written has no mmd scope)
  const { locations: mmdLocations } = useMMDLocations();

  // Functions list for the dropdown — fetched lazily when the dialog opens
  const [functions, setFunctions] = useState([]);
  const [functionsLoading, setFunctionsLoading] = useState(false);

  // Refetch functions when dialog opens or when grade changes (functions are grade-scoped)
  useEffect(() => {
    if (!showTemplateDialog) return;
    let cancelled = false;
    const load = async () => {
      setFunctionsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('exam_type', questionType === 'written' ? 'written' : 'orals');
        if (tplGrade && tplGrade !== 'all') params.set('grade', tplGrade);
        const res = await axios.get(`${API}/functions?${params.toString()}`, { headers });
        if (!cancelled) {
          setFunctions(Array.isArray(res.data) ? res.data : []);
          // If currently selected function is no longer in the list, reset to "all"
          if (tplFunctionId !== 'all') {
            const stillPresent = (res.data || []).some((f) => f.id === tplFunctionId);
            if (!stillPresent) setTplFunctionId('all');
          }
        }
      } catch (err) {
        if (!cancelled) setFunctions([]);
      } finally {
        if (!cancelled) setFunctionsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showTemplateDialog, tplGrade, questionType]);

  // Upload state
  const fileInputRef = useRef(null);
  const [parsing, setParsing] = useState(false);

  // Review modal state
  const [showReview, setShowReview] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewSummary, setPreviewSummary] = useState({ ok: 0, has_existing: 0, blank_skipped: 0, not_found: 0, invalid: 0, duplicate_in_csv: 0, total: 0 });
  const [previewPage, setPreviewPage] = useState(1);
  const previewPerPage = 20;
  const [committing, setCommitting] = useState(false);
  const [markAsAnswered, setMarkAsAnswered] = useState(true);

  // Drawer for long answers
  const [drawerRow, setDrawerRow] = useState(null);

  // ---------- Template download ----------
  const handleDownload = async () => {
    // Validate limit input — empty or positive integer
    let parsedLimit = null;
    if (tplLimit !== '' && tplLimit !== null && tplLimit !== undefined) {
      const n = parseInt(tplLimit, 10);
      if (Number.isNaN(n) || n < 1) {
        toast.error('Number of questions must be a positive integer (or leave blank for All).');
        return;
      }
      parsedLimit = n;
    }
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set('question_type', questionType);
      params.set('unanswered_only', String(tplUnansweredOnly));
      if (tplGrade && tplGrade !== 'all') params.set('grade', tplGrade);
      if (tplFunctionId && tplFunctionId !== 'all') params.set('function_id', tplFunctionId);
      if (questionType === 'oral' && tplMmdLocation && tplMmdLocation !== 'all') {
        params.set('mmd_location', tplMmdLocation);
      }
      if (parsedLimit) params.set('limit', String(parsedLimit));

      const res = await axios.get(`${API}/bulk-answer/template?${params.toString()}`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const selectedFnName = tplFunctionId === 'all'
        ? null
        : (functions.find((f) => f.id === tplFunctionId)?.name || null);
      link.setAttribute('download', buildIstFilename(
        tplGrade === 'all' ? null : tplGrade,
        selectedFnName,
        questionType,
      ));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowTemplateDialog(false);
      toast.success('Template downloaded — fill in the answer_text column and upload it back.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to download template';
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  // ---------- File upload + parse ----------
  const triggerFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are accepted');
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/bulk-answer/parse?question_type=${questionType}`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      const rows = (res.data.preview || []).map((r) => ({
        ...r,
        included: r.status === 'ok' || r.status === 'has_existing',
        action: r.status === 'ok' ? 'apply' : (r.status === 'has_existing' ? 'skip' : 'skip'),
      }));
      setPreviewRows(rows);
      setPreviewSummary(res.data.summary || {});
      setPreviewPage(1);
      setMarkAsAnswered(true);
      setShowReview(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to parse CSV';
      toast.error(msg);
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ---------- Preview manipulation ----------
  const updateRow = (rowNum, patch) => {
    setPreviewRows((prev) => prev.map((r) => (r.row_num === rowNum ? { ...r, ...patch } : r)));
    setDrawerRow((d) => (d && d.row_num === rowNum ? { ...d, ...patch } : d));
  };

  const setAllConflictsAction = (action) => {
    setPreviewRows((prev) =>
      prev.map((r) => (r.status === 'has_existing' ? { ...r, action, included: action !== 'skip' } : r))
    );
  };

  // ---------- Commit ----------
  const commitSummary = useMemo(() => {
    let apply = 0, overwrite = 0, skip = 0;
    for (const r of previewRows) {
      if (r.status !== 'ok' && r.status !== 'has_existing') { skip++; continue; }
      if (!r.included || r.action === 'skip') { skip++; continue; }
      if (r.action === 'apply') apply++;
      else if (r.action === 'overwrite') overwrite++;
    }
    return { apply, overwrite, skip };
  }, [previewRows]);

  const handleCommit = async () => {
    const payload = previewRows
      .filter((r) => r.status === 'ok' || r.status === 'has_existing')
      .map((r) => ({
        question_id: r.question_id,
        answer_text: (r.answer_text || '').trim(),
        action: !r.included ? 'skip' : r.action,
        // Iter 113: forward per-row review flags parsed from the CSV.
        needs_review: !!r.needs_review,
        review_category_id: r.review_category_id || null,
        review_remarks: r.review_remarks || null,
      }));
    if (commitSummary.apply + commitSummary.overwrite === 0) {
      toast.error('Nothing to commit — all rows are set to skip.');
      return;
    }
    setCommitting(true);
    try {
      const res = await axios.post(
        `${API}/bulk-answer/commit`,
        { question_type: questionType, rows: payload },
        { headers }
      );
      const { applied = 0, overwritten = 0, skipped = 0, errors = [] } = res.data || {};
      toast.success(
        `Bulk answer complete: ${applied} applied, ${overwritten} overwritten, ${skipped} skipped${errors.length ? `, ${errors.length} errors` : ''}.`
      );
      setShowReview(false);
      setPreviewRows([]);
      setDrawerRow(null);
      if (onCommitted) onCommitted();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to commit answers';
      toast.error(msg);
    } finally {
      setCommitting(false);
    }
  };

  // Pagination slice
  const previewTotal = previewRows.length;
  const previewSlice = previewRows.slice((previewPage - 1) * previewPerPage, previewPage * previewPerPage);

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'ok':
        return <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50 text-xs">New</Badge>;
      case 'has_existing':
        return <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50 text-xs">Has existing</Badge>;
      case 'blank_skipped':
        return <Badge variant="outline" className="text-sky-700 border-sky-200 bg-sky-50 text-xs">Blank (skipped)</Badge>;
      case 'not_found':
        return <Badge variant="outline" className="text-gray-600 border-gray-300 bg-gray-50 text-xs">Not found</Badge>;
      case 'duplicate_in_csv':
        return <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-xs">Duplicate</Badge>;
      case 'invalid':
        return <Badge variant="outline" className="text-gray-600 border-gray-200 bg-gray-50 text-xs">Invalid</Badge>;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Triggers — either as standalone buttons (default) or as menu items
          for the parent's "More actions" dropdown. */}
      {compact ? (
        <>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); setShowTemplateDialog(true); }}
            className="text-blue-700 focus:bg-blue-50"
            data-testid={`bulk-answer-template-menu-${questionType}`}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Bulk Answer Template
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={parsing}
            onSelect={(e) => { e.preventDefault(); triggerFilePicker(); }}
            className="text-emerald-700 focus:bg-emerald-50"
            data-testid={`bulk-answer-upload-menu-${questionType}`}
          >
            {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Bulk Answer Upload
          </DropdownMenuItem>
        </>
      ) : (
        <>
          <Button
            variant="outline"
            onClick={() => setShowTemplateDialog(true)}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid={`bulk-answer-template-btn-${questionType}`}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Bulk Answer Template
          </Button>
          <Button
            variant="outline"
            onClick={triggerFilePicker}
            disabled={parsing}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid={`bulk-answer-upload-btn-${questionType}`}
          >
            {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Bulk Answer Upload
          </Button>
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFilePick}
        className="hidden"
        data-testid={`bulk-answer-file-input-${questionType}`}
      />

      {/* Template Download Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={(o) => { if (!downloading) setShowTemplateDialog(o); }}>
        <DialogContent className="max-w-md" data-testid="bulk-answer-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Download Bulk Answer Template
            </DialogTitle>
            <DialogDescription>
              {questionType === 'oral' ? (
                <>Exports a CSV with 6 columns: question_id, grade, mmd_location, function, question_text, answer_text. Fill in the answer_text column and upload it back to apply answers in bulk.</>
              ) : (
                <>Exports a CSV with 5 columns: question_id, grade, function, question_text, answer_text. Fill in the answer_text column and upload it back to apply answers in bulk.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm">Grade filter</Label>
              <Select value={tplGrade} onValueChange={(v) => { setTplGrade(v); setTplFunctionId('all'); }}>
                <SelectTrigger data-testid="template-grade-select"><SelectValue placeholder="All grades" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All grades</SelectItem>
                  {GRADE_OPTIONS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {questionType === 'oral' && (
              <div>
                <Label className="text-sm">MMD location filter</Label>
                <Select value={tplMmdLocation} onValueChange={setTplMmdLocation}>
                  <SelectTrigger data-testid="template-mmd-select">
                    <SelectValue placeholder="All MMDs" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All MMDs</SelectItem>
                    {(mmdLocations || []).map((mmd) => (
                      <SelectItem key={mmd} value={mmd}>{mmd}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Filter the template to a single MMD so you can answer one centre at a time.
                </p>
              </div>
            )}

            <div>
              <Label className="text-sm flex items-center gap-2">
                Function filter
                {functionsLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
              </Label>
              <Select value={tplFunctionId} onValueChange={setTplFunctionId} disabled={functionsLoading}>
                <SelectTrigger data-testid="template-function-select">
                  <SelectValue placeholder={functionsLoading ? 'Loading…' : 'All functions'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All functions</SelectItem>
                  {functions.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name || f.title || f.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!functionsLoading && functions.length === 0 && (
                <p className="text-[11px] text-gray-500 mt-1">
                  No functions available{tplGrade !== 'all' ? ` for ${tplGrade}` : ''}.
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm">Number of questions</Label>
              <Input
                type="number"
                min={1}
                placeholder="All"
                value={tplLimit}
                onChange={(e) => setTplLimit(e.target.value)}
                className="mt-1"
                data-testid="template-limit-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Leave blank for all matching questions. If the number entered is more than available, the full set is exported.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={tplUnansweredOnly}
                onCheckedChange={(v) => setTplUnansweredOnly(!!v)}
                id="tpl-unanswered-only"
                data-testid="template-unanswered-checkbox"
              />
              <Label htmlFor="tpl-unanswered-only" className="text-sm cursor-pointer">
                Only export unanswered questions (recommended)
              </Label>
            </div>

            <p className="text-xs text-gray-500 border-l-2 border-blue-200 pl-3">
              <strong>Tip:</strong> the question_id column is the join key — don&apos;t edit it. Grade and
              function are shown for context, to help you calibrate the depth of each answer.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)} disabled={downloading}>
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={downloading} className="bg-blue-600 hover:bg-blue-700"
              data-testid="template-download-confirm-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={showReview} onOpenChange={(o) => { if (!committing) setShowReview(o); }}>
        <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col" data-testid="bulk-answer-review-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600" />
              Review Bulk Answers — {questionType === 'oral' ? 'Oral' : 'Written'}
            </DialogTitle>
            <DialogDescription>
              The file was parsed in-memory and discarded immediately — nothing is stored on the server until you confirm.
              Edit answers inline if needed; click the pencil to open a larger editor for long answers.
            </DialogDescription>
          </DialogHeader>

          {/* Summary chips */}
          <div className="flex flex-wrap items-center gap-2 px-1">
            <Badge variant="outline" className="text-gray-700">Total parsed: {previewSummary.total}</Badge>
            <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">
              <CheckCircle2 className="w-3 h-3 mr-1" /> New: {previewSummary.ok}
            </Badge>
            <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">
              <AlertTriangle className="w-3 h-3 mr-1" /> Has existing: {previewSummary.has_existing}
            </Badge>
            <Badge variant="outline" className="text-sky-700 border-sky-200 bg-sky-50">
              Blank (skipped): {previewSummary.blank_skipped || 0}
            </Badge>
            <Badge variant="outline" className="text-gray-600 border-gray-300 bg-gray-50">
              Not found: {previewSummary.not_found}
            </Badge>
            <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
              Duplicate in CSV: {previewSummary.duplicate_in_csv}
            </Badge>
            <Badge variant="outline" className="text-gray-500 border-gray-200">
              Invalid: {previewSummary.invalid}
            </Badge>

            {previewSummary.has_existing > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-gray-500">Existing-answer rows:</span>
                <Button size="sm" variant="outline" onClick={() => setAllConflictsAction('overwrite')}
                  className="h-7 px-2 text-xs" data-testid="bulk-overwrite-all-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Overwrite all
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAllConflictsAction('skip')}
                  className="h-7 px-2 text-xs" data-testid="bulk-skip-all-btn">
                  Skip all
                </Button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto border rounded-lg mt-3">
            <table className="w-full text-sm" data-testid="bulk-answer-preview-table">
              <thead className="sticky top-0 bg-gray-50 border-b z-10">
                <tr>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-12">Use</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-14">Row</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[120px]">Grade</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[120px]">Function</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[240px]">Question</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase">Answer</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[130px]">Status</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[110px]">Review flag</th>
                  <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[160px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {previewSlice.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-gray-400 text-sm">No rows parsed.</td></tr>
                ) : previewSlice.map((r) => {
                  const isConflict = r.status === 'has_existing';
                  const isBad = r.status === 'invalid' || r.status === 'not_found' || r.status === 'duplicate_in_csv' || r.status === 'blank_skipped';
                  const ansLong = (r.answer_text || '').length > INLINE_PREVIEW_CHARS;
                  return (
                    <tr key={r.row_num}
                      className={`border-b transition-colors ${isConflict ? 'bg-red-50/60' : isBad ? 'bg-gray-50' : 'hover:bg-gray-50/50'}`}
                      data-testid={`bulk-row-${r.row_num}`}>
                      <td className="p-2 align-top">
                        <Checkbox
                          checked={!!r.included}
                          disabled={isBad}
                          onCheckedChange={(v) => updateRow(r.row_num, { included: !!v })}
                          data-testid={`bulk-include-${r.row_num}`}
                        />
                      </td>
                      <td className="p-2 align-top text-xs text-gray-500 font-mono">{r.row_num}</td>
                      <td className="p-2 align-top text-xs text-gray-700">{r.grade || '—'}</td>
                      <td className="p-2 align-top text-xs text-gray-700">{r.function || '—'}</td>
                      <td className="p-2 align-top">
                        <div className="text-xs text-gray-600 line-clamp-3 whitespace-pre-wrap">
                          {r.question_text || '—'}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1 font-mono truncate" title={r.question_id}>
                          id: {r.question_id || '—'}
                        </div>
                      </td>
                      <td className="p-2 align-top">
                        <div className="flex items-start gap-1">
                          {ansLong ? (
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-700 line-clamp-2 whitespace-pre-wrap">
                                {r.answer_text}
                              </div>
                              <button
                                onClick={() => setDrawerRow(r)}
                                className="text-[11px] text-emerald-700 hover:underline mt-1 inline-flex items-center"
                                data-testid={`bulk-open-drawer-${r.row_num}`}
                              >
                                <Pencil className="w-3 h-3 mr-0.5" /> View / Edit
                              </button>
                            </div>
                          ) : (
                            <Textarea
                              value={r.answer_text || ''}
                              disabled={isBad}
                              onChange={(e) => updateRow(r.row_num, { answer_text: e.target.value })}
                              className="min-h-[60px] text-xs resize-none"
                              data-testid={`bulk-answer-input-${r.row_num}`}
                            />
                          )}
                        </div>
                        {isConflict && r.existing_answer && (
                          <div className="text-[10px] text-red-700 mt-1 line-clamp-2 italic">
                            existing: {r.existing_answer.slice(0, 120)}{r.existing_answer.length > 120 ? '…' : ''}
                          </div>
                        )}
                      </td>
                      <td className="p-2 align-top">
                        {renderStatusBadge(r.status)}
                        {r.message && (
                          <div className="text-[10px] text-gray-500 mt-1">{r.message}</div>
                        )}
                      </td>
                      <td className="p-2 align-top">
                        {r.needs_review ? (
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px] w-fit">Review</Badge>
                            {r.review_category_raw && (
                              <span className={`text-[10px] ${r.review_category_unknown ? 'text-red-500' : 'text-gray-600'}`}>
                                {r.review_category_raw}{r.review_category_unknown ? ' (unknown cat.)' : ''}
                              </span>
                            )}
                          </div>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                      <td className="p-2 align-top">
                        {r.status === 'ok' && <span className="text-xs text-green-700">Will apply</span>}
                        {r.status === 'has_existing' && (
                          <div className="flex items-center gap-1 flex-wrap">
                            <Button size="sm" variant={r.action === 'overwrite' ? 'default' : 'outline'}
                              onClick={() => updateRow(r.row_num, { action: 'overwrite', included: true })}
                              className={`h-7 px-2 text-xs ${r.action === 'overwrite' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                              data-testid={`bulk-row-overwrite-${r.row_num}`}>
                              Overwrite
                            </Button>
                            <Button size="sm" variant={r.action === 'skip' ? 'default' : 'outline'}
                              onClick={() => updateRow(r.row_num, { action: 'skip', included: false })}
                              className={`h-7 px-2 text-xs ${r.action === 'skip' ? 'bg-gray-500 hover:bg-gray-600' : ''}`}
                              data-testid={`bulk-row-skip-${r.row_num}`}>
                              Skip
                            </Button>
                          </div>
                        )}
                        {isBad && <span className="text-xs text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer: commit summary + mark-as-answered toggle + pagination */}
          <div className="flex flex-col gap-3 mt-3 px-1">
            <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded">
              <Checkbox
                id="bulk-mark-answered"
                checked={markAsAnswered}
                onCheckedChange={(v) => setMarkAsAnswered(!!v)}
                data-testid="bulk-mark-answered-toggle"
              />
              <Label htmlFor="bulk-mark-answered" className="text-xs cursor-pointer flex-1 leading-relaxed">
                <span className="font-medium text-emerald-900">Also mark these as &quot;Answered&quot;</span>
                <span className="text-emerald-700"> — publishes the answers to users. Uncheck to bulk-upload as drafts (text saved, but stays in &quot;Coming Soon&quot; until you manually mark each as answered).</span>
              </Label>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="text-xs text-gray-600">
                <span className="text-green-700 font-medium">{commitSummary.apply}</span> to apply, {' '}
                <span className="text-red-700 font-medium">{commitSummary.overwrite}</span> to overwrite, {' '}
                <span className="text-gray-500 font-medium">{commitSummary.skip}</span> to skip
                {(previewSummary.blank_skipped || 0) > 0 && (
                  <span className="text-sky-700 ml-2">
                    · {previewSummary.blank_skipped} blank row{previewSummary.blank_skipped === 1 ? '' : 's'} preserved
                  </span>
                )}
              </div>
              {previewTotal > previewPerPage && (
                <Pagination
                  currentPage={previewPage}
                  totalItems={previewTotal}
                  itemsPerPage={previewPerPage}
                  onPageChange={setPreviewPage}
                  onItemsPerPageChange={() => {}}
                />
              )}
            </div>
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setShowReview(false)} disabled={committing}
              data-testid="bulk-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleCommit}
              disabled={committing || (commitSummary.apply + commitSummary.overwrite === 0)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-confirm-btn">
              {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Apply Answers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drawer for long answers */}
      <Sheet open={!!drawerRow} onOpenChange={(o) => { if (!o) setDrawerRow(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="bulk-answer-drawer">
          {drawerRow && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 text-base">
                  <ChevronRight className="w-4 h-4" />
                  Row {drawerRow.row_num} — Edit Answer
                </SheetTitle>
                <SheetDescription className="text-xs space-y-1">
                  <div><strong>Grade:</strong> {drawerRow.grade || '—'} · <strong>Function:</strong> {drawerRow.function || '—'}</div>
                  <div className="font-mono text-[10px] text-gray-400 truncate">id: {drawerRow.question_id}</div>
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="text-xs uppercase text-gray-500">Question</Label>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap p-3 bg-gray-50 border rounded mt-1">
                    {drawerRow.question_text || '—'}
                  </div>
                </div>
                {drawerRow.existing_answer && (
                  <div>
                    <Label className="text-xs uppercase text-red-600">Existing answer in DB</Label>
                    <div className="text-sm text-red-800 whitespace-pre-wrap p-3 bg-red-50 border border-red-200 rounded mt-1 max-h-48 overflow-y-auto">
                      {drawerRow.existing_answer}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase text-gray-500">New answer (editable)</Label>
                  <Textarea
                    value={drawerRow.answer_text || ''}
                    onChange={(e) => updateRow(drawerRow.row_num, { answer_text: e.target.value })}
                    className="mt-1 min-h-[300px] text-sm"
                    data-testid="bulk-drawer-answer-input"
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => setDrawerRow(null)} className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="bulk-drawer-done-btn">
                    Done
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default BulkAnswerControls;
